import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  IoHomeOutline, 
  IoSettingsOutline, 
  IoPeopleOutline, 
  IoPersonOutline,
  IoBusinessOutline,
  IoLibraryOutline,
  IoDocumentTextOutline,
  IoStatsChartOutline,
  IoNotificationsOutline,
  IoFlashOutline,
  IoTrophyOutline,
  IoFlagOutline,
  IoBookOutline,
  IoArrowUndoOutline,
  IoLogOutOutline,
  IoMenuOutline,
  IoCloseOutline,
  IoChevronBackOutline, IoPodiumOutline } from 'react-icons/io5';
import './AdminLayout.css';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const adminNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: IoStatsChartOutline, path: '/admin/dashboard' },
    { id: 'families', label: 'Family Groups', icon: IoPeopleOutline, path: '/admin/families' },
    { id: 'churches', label: 'Churches', icon: IoBusinessOutline, path: '/admin/churches' },
    { id: 'users', label: 'User Management', icon: IoPersonOutline, path: '/admin/users' },
    { id: 'readers', label: 'Readers', icon: IoLibraryOutline, path: '/admin/readers' },
    { id: 'challenges', label: 'Challenges', icon: IoFlagOutline, path: '/admin/challenges' },
    { id: 'leaderboards', label: 'Leaderboards', icon: IoPodiumOutline, path: '/admin/leaderboards' },
    { id: 'goals', label: 'Site Goals', icon: IoTrophyOutline, path: '/admin/goals' },
    { id: 'reports', label: 'Reading Reports', icon: IoBookOutline, path: '/admin/reports' },
    { id: 'undo-reports', label: 'Undo Reports', icon: IoArrowUndoOutline, path: '/admin/undo-reports' },
    { id: 'notifications', label: 'Notifications', icon: IoNotificationsOutline, path: '/admin/notifications' },
    { id: 'events', label: 'Events', icon: IoFlashOutline, path: '/admin/events' },
    { id: 'audit', label: 'Audit Log', icon: IoDocumentTextOutline, path: '/admin/audit' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  const handleLogout = async () => {
    await logout();
    setSidebarOpen(false);
  };

  const currentPath = location.pathname;
  // Prefix match, so a sub-path or a page with a query string keeps its highlight
  const isCurrent = (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`);
  const currentNavItem = adminNavItems.find(isCurrent);

  return (
    <div className="admin-layout">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <button 
            className="mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
          >
            <IoMenuOutline />
          </button>
          <button 
            className="back-to-main-button"
            onClick={handleBackToMain}
            title="Back to Main Site"
          >
            <IoChevronBackOutline />
            <span>Back to Site</span>
          </button>
          <div className="admin-title">
            <h1>Admin Panel</h1>
            {currentNavItem && (
              <span className="current-page">{currentNavItem.label}</span>
            )}
          </div>
        </div>
        <div className="admin-header-right">
          <div className="admin-user-info">
            <span className="admin-user-name">{user?.name}</span>
            <span className="admin-badge">Admin</span>
          </div>
          <button 
            className="logout-button"
            onClick={handleLogout}
            title="Logout"
          >
            <IoLogOutOutline />
          </button>
        </div>
      </header>

      <div className="admin-content-wrapper">
        {/* Admin Sidebar */}
        <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="admin-sidebar-header">
            <h2>Admin Menu</h2>
            <button 
              className="close-sidebar-button"
              onClick={() => setSidebarOpen(false)}
            >
              <IoCloseOutline />
            </button>
          </div>
          
          <nav className="admin-nav">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = isCurrent(item);
              
              return (
                <button
                  key={item.id}
                  className={`admin-nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.path)}
                >
                  <Icon className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="admin-sidebar-footer">
            <button 
              className="back-to-main-sidebar"
              onClick={handleBackToMain}
            >
              <IoHomeOutline />
              <span>Back to Main Site</span>
            </button>
          </div>
        </aside>

        {/* Admin Main Content */}
        <main className="admin-main">
          {children}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="admin-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
